import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { DocumentDetail, DocumentPageListItem } from '@avisoma//shared';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePersistedState } from '../hooks/usePersistetState';
import { DocumentPageRow } from './documentPages/DocumentPageRow';
import { DocumentPagesHeaderFilters } from './documentPages/DocumentPagesHeaderFilters';
import {
  doesPageHaveError,
  doesPageHaveLowClassificationScore,
  isPageProcessing,
} from './documentPages/documentPageUtils';

export interface DocumentPagesProps {
  document: DocumentDetail;
  pagesQuery: QueryObserverResult<DocumentPageListItem[]>;
  requestRelad: () => void;
  /** pageId -> stranded stage; supplied by DocumentView, consumed in Task 8 to badge stranded rows. */
  strandedByPage?: Map<string, string>;
}

/**
 * DR-2427: estimated height (px) of a collapsed page row before it measures itself. Each
 * row mounts a LazyImage + two LazyContainers plus a processing sidebar; real heights vary,
 * so the virtualizer measures each mounted row (measureElement) and corrects the estimate.
 * Only viewport rows (+overscan) mount, so an 8k-page document never freezes the tab.
 */
const ESTIMATED_ROW_HEIGHT = 500;
const ROW_OVERSCAN = 5;

export interface DocumentPagesRef {
  scrollToPage: (pageIndex: number) => void;
}

function DocumentPages(
  { document, pagesQuery, requestRelad, strandedByPage }: DocumentPagesProps,
  ref: React.Ref<DocumentPagesRef>,
) {
  // Memoized so `?? []` doesn't hand a fresh array to every downstream memo/callback on each
  // render (react-query keeps `data` referentially stable between refetches).
  const pages = useMemo(() => pagesQuery.data ?? [], [pagesQuery.data]);
  const loadingPages = pagesQuery.isLoading;
  const pagesError = pagesQuery.error?.message ?? null;

  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [showOnlyNoCategory, setShowOnlyNoCategory] = useState(false);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [showOnlyStranded, setShowOnlyStranded] = useState(false);
  const [showAdvancedView, setShowAdvancedView] = usePersistedState('showAdvancedView', false);
  const [openDropdownPageId, setOpenDropdownPageId] = useState<string | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showOnlyLowClassificationScore, setShowOnlyLowClassificationScore] = useState(false);
  const [showOnlyProcessing, setShowOnlyProcessing] = useState(false);

  // DR-2426: hoisted once per page-list identity so every row shares the SAME array
  // reference (was rebuilt per row → O(n) allocation × n rows) and the full-document
  // page number is an O(1) map lookup instead of `pages.indexOf(page)` per row (O(n²)).
  const pageIds = useMemo(() => pages.map((p) => p.id), [pages]);
  const pageIndexById = useMemo(() => new Map(pages.map((p, i) => [p.id, i])), [pages]);

  // Stable identity so a re-render that passes a fresh `requestRelad` closure (e.g. a poll
  // refetch in the parent) doesn't re-render every memoized row. Latest-ref pattern: the
  // ref is updated in an effect (never during render), so it doesn't trip the compiler's
  // refs-during-render rule.
  const requestReladRef = useRef(requestRelad);
  useEffect(() => {
    requestReladRef.current = requestRelad;
  }, [requestRelad]);
  const stableRequestRelad = useCallback(() => requestReladRef.current(), []);

  const handleDropdownToggle = useCallback(
    (pageId: string) => setOpenDropdownPageId((prev) => (prev === pageId ? null : pageId)),
    [],
  );

  const pagesWithErrors = useMemo(() => pages.filter((page) => doesPageHaveError(page)), [pages]);

  const pagesWithLowClassificationScore = useMemo(
    () => pages.filter((page) => doesPageHaveLowClassificationScore(page)),
    [pages],
  );

  const pagesWithNoCategory = useMemo(() => pages.filter((page) => !page.category), [pages]);

  const pagesProcessing = useMemo(() => pages.filter((page) => isPageProcessing(page)), [pages]);

  useEffect(() => {
    if (showOnlyProcessing && pagesProcessing.length === 0) {
      setShowOnlyProcessing(false);
    }
  }, [showOnlyProcessing, pagesProcessing.length]);

  const filteredPages = useMemo(() => {
    return pages.filter((page) => {
      if (showOnlyNoCategory && page.category) {
        return false;
      }

      if (showOnlyErrors && !doesPageHaveError(page)) {
        return false;
      }

      if (showOnlyStranded && !strandedByPage?.has(page.id)) {
        return false;
      }

      if (showOnlyProcessing && !isPageProcessing(page)) {
        return false;
      }

      if (showOnlyLowClassificationScore) {
        return doesPageHaveLowClassificationScore(page);
      }

      return true;
    });
  }, [
    showOnlyErrors,
    showOnlyStranded,
    strandedByPage,
    showOnlyNoCategory,
    pages,
    pagesQuery.dataUpdatedAt,
    showOnlyLowClassificationScore,
    showOnlyProcessing,
  ]);

  // DR-2427: window the filtered rows — only viewport rows (+overscan) mount. Track items by
  // page id (getItemKey) so measured heights follow a page across filter/refetch changes
  // instead of sticking to a stale index. estimateSize seeds the layout; measureElement
  // corrects each row once it mounts.
  const rowVirtualizer = useVirtualizer({
    count: filteredPages.length,
    getScrollElement: () => pagesContainerRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: ROW_OVERSCAN,
    getItemKey: (index) => filteredPages[index]?.id ?? index,
  });

  // DR-2427: scroll a page into view. Callers pass an index into the FULL page list; resolve
  // it to the page id, locate that page in the CURRENT filtered list (no-op if the active
  // filter hides it), then hand the position to the virtualizer, which mounts, measures, and
  // re-aligns the target row even when it was outside the rendered window.
  const scrollToPage = useCallback(
    (pageIndex: number) => {
      const targetId = pages[pageIndex]?.id;
      if (!targetId) return;
      const filteredPos = filteredPages.findIndex((p) => p.id === targetId);
      if (filteredPos < 0) return; // hidden by the active filter → no-op, as before
      rowVirtualizer.scrollToIndex(filteredPos, { align: 'start' });
    },
    [pages, filteredPages, rowVirtualizer],
  );

  useImperativeHandle(ref, () => ({ scrollToPage }), [scrollToPage]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <div>
      <DocumentPagesHeaderFilters
        filteredCount={filteredPages.length}
        totalCount={pages.length}
        showFilterDropdown={showFilterDropdown}
        setShowFilterDropdown={setShowFilterDropdown}
        showAdvancedView={showAdvancedView}
        setShowAdvancedView={setShowAdvancedView}
        showOnlyNoCategory={showOnlyNoCategory}
        setShowOnlyNoCategory={setShowOnlyNoCategory}
        showOnlyErrors={showOnlyErrors}
        setShowOnlyErrors={setShowOnlyErrors}
        showOnlyStranded={showOnlyStranded}
        setShowOnlyStranded={setShowOnlyStranded}
        strandedCount={strandedByPage?.size ?? 0}
        showOnlyLowClassificationScore={showOnlyLowClassificationScore}
        setShowOnlyLowClassificationScore={setShowOnlyLowClassificationScore}
        showOnlyProcessing={showOnlyProcessing}
        setShowOnlyProcessing={setShowOnlyProcessing}
        pagesWithNoCategoryCount={pagesWithNoCategory.length}
        pagesWithErrorsCount={pagesWithErrors.length}
        pagesWithLowClassificationScoreCount={pagesWithLowClassificationScore.length}
        pagesProcessingCount={pagesProcessing.length}
      />

      <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 dark:shadow-zinc-950/30 shadow sm:rounded-lg mb-6 overflow-hidden">
        {loadingPages && pages.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-gray-500 dark:text-zinc-400">Loading pages...</div>
          </div>
        ) : pagesError ? (
          <div className="text-center py-4">
            <div className="text-red-500 dark:text-red-400">{pagesError}</div>
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-gray-500 dark:text-zinc-400">
              {showOnlyErrors ? 'No pages with errors' : 'No pages available'}
            </div>
          </div>
        ) : (
          <div
            ref={pagesContainerRef}
            className="max-h-[800px] bg-neutral-500 text-white dark:text-zinc-100 overflow-y-auto dark:bg-zinc-900"
          >
            <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
              {virtualRows.map((virtualRow) => {
                const page = filteredPages[virtualRow.index];
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <DocumentPageRow
                      documentId={document.id}
                      page={page}
                      pageNumber={(pageIndexById.get(page.id) ?? 0) + 1}
                      pageIds={pageIds}
                      showAdvancedView={showAdvancedView}
                      isDropdownOpen={openDropdownPageId === page.id}
                      onDropdownToggle={handleDropdownToggle}
                      requestRelad={stableRequestRelad}
                      strandedStage={strandedByPage?.get(page.id)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const DocumentPagesWrapped = React.memo(React.forwardRef<DocumentPagesRef, DocumentPagesProps>(DocumentPages));

export default DocumentPagesWrapped;
